import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { PanelLoading } from "../../components/Loading";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  procurementContractsApi,
  CONTRACT_DOC_ALLOWED,
  CONTRACT_DOC_MAX_BYTES,
  CONTRACT_DOC_MAX_FILES,
  type ProcurementContract,
  type ProcurementContractAmendment,
  type ProcurementContractDocument,
  type ProcurementContractDocumentKind,
} from "../../api/procurementContracts";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { Select } from "../../components/Select";
import { FulfilmentBar } from "../../components/FulfilmentBar";
import { StatusBadge } from "./ProcurementContractsPage";
import Ic from "../../components/icons.jsx";

const DOC_KINDS: ProcurementContractDocumentKind[] = ["CONTRACT", "ANNEX", "OTHER"];

/**
 * Ugovor o otkupu — detail view.
 *
 * This page is
 * where the scanned contract and its annexes live, and where fulfilment is shown
 * broken down rather than as a single bar.
 *
 * Uploading needs `procurement_contracts:documents`; reading rides on
 * `procurement_contracts:view`, so the supplier's own people and the sales
 * director (read-only insight) see the papers without the upload UI. Every
 * write also needs the contract's `canAct`: the otkup desk reads a colleague's
 * supplier but does not act on it.
 */
/**
 * Rok utovara onako kako je dogovoren: prozor, jedan datum, ili rečima.
 *
 * Opisni rok („u toku žetve", ime meseca) nije datum i ne pravi se datum od njega — na Bankomovom
 * registru takav je svaki četvrti ugovor.
 */
function utovar(c: ProcurementContract, fmtDate: (v: string | null) => string): string {
  const od = c.loadingFrom ? fmtDate(c.loadingFrom) : null;
  const doo = c.loadingTo ? fmtDate(c.loadingTo) : null;
  const opseg = od && doo ? `${od} – ${doo}` : (od ?? doo);
  if (opseg && c.loadingNote) return `${opseg} (${c.loadingNote})`;
  return opseg ?? c.loadingNote ?? "—";
}

/**
 * Rok plaćanja sa sidrom od kog se računa.
 *
 * Broj dana bez sidra ne znači ništa: „7 dana" je na istom registru i po utovaru i po isporuci, a
 * razlika je nekoliko dana robe na putu.
 */
function placanje(
  c: ProcurementContract,
  t: TFunction,
  fmtDate: (v: string | null) => string,
): string {
  if (c.paymentBase === "FIXED_DATE") {
    return c.paymentDueDate
      ? t("procurement.contract.payment.fixed", "do {{datum}}", { datum: fmtDate(c.paymentDueDate) })
      : "—";
  }
  if (c.paymentDays == null) return "—";
  const sidro =
    c.paymentBase === "LOADING"
      ? t("procurement.contract.payment.loading", "po utovaru")
      : c.paymentBase === "DELIVERY"
        ? t("procurement.contract.payment.delivery", "po isporuci")
        : c.paymentBase === "INVOICE"
          ? t("procurement.contract.payment.invoice", "po fakturi")
          : null;
  const dana = t("procurement.contract.payment.days", "{{n}} dana", { n: c.paymentDays });
  return sidro ? `${dana} ${sidro}` : dana;
}

export default function ProcurementContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission, hasRole } = useAuth();

  const [contract, setContract] = useState<ProcurementContract | null>(null);
  const [documents, setDocuments] = useState<ProcurementContractDocument[]>([]);
  const [amendments, setAmendments] = useState<ProcurementContractAmendment[]>([]);
  const [amending, setAmending] = useState(false);
  const [kind, setKind] = useState<ProcurementContractDocumentKind>("CONTRACT");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const canManageDocs = hasPermission("procurement_contracts:documents") && contract?.canAct === true;
  const isDirector = hasPermission("section:procurement_contracts:full") && (hasRole("ADMIN") || hasRole("SUPERADMIN"));
  // An amendment rewrites the terms of a contract in force — the director's
  // call once ACTIVE; the referent still amends a draft. Mirror of the server rule.
  const canAmend =
    hasPermission("procurement_contracts:amend") &&
    contract != null &&
    contract.canAct &&
    contract.status !== "FULFILLED" &&
    contract.status !== "CANCELLED" &&
    (contract.status !== "ACTIVE" || isDirector);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const c = await procurementContractsApi.get(id);
      setContract(c);
      setDocuments(await procurementContractsApi.listDocuments(id));
      setAmendments(await procurementContractsApi.listAmendments(id));
    } catch (e) {
      setError((e as ApiError).message);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleFile(f: File) {
    if (!id) return;
    if (!CONTRACT_DOC_ALLOWED.includes(f.type)) {
      toast.error(t("procurement.document.badType", "File type not allowed."));
      return;
    }
    if (f.size > CONTRACT_DOC_MAX_BYTES) {
      toast.error(t("procurement.document.tooLarge", "File exceeds 25 MB."));
      return;
    }
    if (documents.length >= CONTRACT_DOC_MAX_FILES) {
      toast.error(t("procurement.document.quotaReached", "Max 10 documents per contract."));
      return;
    }
    setBusy(true);
    try {
      const presigned = await procurementContractsApi.presignDocument(id, {
        fileName: f.name,
        contentType: f.type,
        sizeBytes: f.size,
        kind,
      });
      const res = await fetch(presigned.url, {
        method: "PUT",
        headers: { "Content-Type": f.type },
        body: f,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      await procurementContractsApi.confirmDocument(id, {
        objectKey: presigned.objectKey,
        fileName: f.name,
        contentType: f.type,
        sizeBytes: f.size,
        kind,
      });
      toast.success(t("procurement.document.uploaded", "Document uploaded."));
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function download(d: ProcurementContractDocument) {
    if (!id) return;
    try {
      const { url } = await procurementContractsApi.documentDownloadUrl(id, d.id);
      window.open(url, "_blank");
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function remove(d: ProcurementContractDocument) {
    if (!id) return;
    const ok = await confirm({
      message: t("procurement.document.confirmDelete", "Delete {{name}}?", { name: d.fileName }),
      danger: true,
    });
    if (!ok) return;
    try {
      await procurementContractsApi.deleteDocument(id, d.id);
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleDateString(i18n.resolvedLanguage) : "—");

  if (error) return <div className="page"><p style={{ color: "var(--err)" }}>{error}</p></div>;
  if (!contract) return <div className="page"><PanelLoading text={t("common.loading")} /></div>;

  return (
    <div className="page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <nav style={{ display: "flex", gap: 6, fontSize: 12 }}>
        <Link to="/procurement/contracts" style={crumbLink}>
          {t("nav.otkup.contracts", "Procurement contracts")}
        </Link>
        <span style={crumbMuted}>/</span>
        <span style={crumbCurrent}>{contract.referenceNumber}</span>
      </nav>

      <header style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0 }}>{contract.referenceNumber}</h1>
        <StatusBadge status={contract.status} />
        <span style={{ color: "var(--ink-3)" }}>{contract.companyName}</span>
      </header>

      <section style={card}>
        <h2 style={cardTitle}>{t("procurement.contract.fulfilment", "Fulfilment")}</h2>
        <FulfilmentBar
          settledT={contract.settledT}
          inProgressT={contract.inProgressT}
          remainingT={contract.remainingT}
          fulfilmentPct={contract.fulfilmentPct}
        />
        {contract.readyToClose && (
          <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
            {t(
              "procurement.contract.readyToCloseHint",
              "Contracted quantity reached — the contract is closed by hand.",
            )}
          </p>
        )}
      </section>

      <section style={card}>
        <h2 style={cardTitle}>{t("procurement.contract.terms", "Contract terms")}</h2>
        <div style={grid}>
          <ReadField label={t("procurement.contracts.list.col.culture")} value={contract.culture ?? "—"} />
          <ReadField
            label={t("procurement.contracts.list.col.quantity")}
            value={`${contract.quantityT} t`}
          />
          <ReadField
            label={t("procurement.contracts.list.col.price")}
            value={contract.pricePerT != null ? `${contract.pricePerT} / t` : "—"}
          />
          <ReadField
            label={t("procurement.contracts.list.col.harvestYear")}
            value={String(contract.harvestYear)}
          />
          <ReadField
            label={t("procurement.contracts.list.col.effective")}
            value={fmtDate(contract.effectiveDate)}
          />
          <ReadField
            label={t("procurement.contracts.list.col.expires")}
            value={fmtDate(contract.expiresAt)}
          />
          <ReadField
            label={t("procurement.contract.contractDate", "Datum ugovora")}
            value={fmtDate(contract.contractDate)}
          />
          <ReadField
            label={t("procurement.contract.parity", "Paritet")}
            value={contract.parity ?? "—"}
          />
          <ReadField
            label={t("procurement.contract.loading", "Utovar")}
            value={utovar(contract, fmtDate)}
          />
          <ReadField
            label={t("procurement.contract.payment", "Plaćanje")}
            value={placanje(contract, t, fmtDate)}
          />
        </div>
        {contract.sourceNote && (
          /* Tekst iz kog su uslovi pročitani. Stoji uz njih zato što su izvedeni iz jedne ćelije
             slobodnog teksta, pa svako polje mora da se može proveriti prema onome što je napisano. */
          <p style={{ margin: "8px 0 0", fontSize: 11, color: "var(--ink-3)", overflowWrap: "anywhere" }}>
            {t("procurement.contract.sourceNote", "Iz izvora:")} {contract.sourceNote}
          </p>
        )}
      </section>

      <section style={card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <h2 style={cardTitle}>{t("procurement.amendment.title", "Term changes")}</h2>
          {canAmend && (
            <button type="button" className="btn sm" onClick={() => setAmending(true)}>
              <Ic.plus /> {t("procurement.amendment.new", "New amendment")}
            </button>
          )}
        </div>
        <p style={{ margin: 0, fontSize: 11, color: "var(--ink-3)" }}>
          {t("procurement.amendment.hint", "An amendment is never deleted — it is corrected by a further amendment.")}
        </p>
        {amendments.length === 0 ? (
          <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
            {t("procurement.amendment.empty", "Terms have not been changed.")}
          </p>
        ) : (
          <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {amendments.map((a) => (
              <li key={a.id} style={docRow}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13, color: "var(--ink)" }}>
                    {[
                      a.quantityT != null
                        ? `${t("procurement.contracts.list.col.quantity")}: ${a.quantityT} t`
                        : null,
                      a.pricePerT != null
                        ? `${t("procurement.contracts.list.col.price")}: ${a.pricePerT}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
                    {t("procurement.amendment.effectiveDate", "Effective from")} {fmtDate(a.effectiveDate)}
                    {a.note ? ` · ${a.note}` : ""}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}
        {canAmend && amending && id && (
          <AmendmentForm
            contractId={id}
            onClose={() => setAmending(false)}
            onSaved={async () => {
              setAmending(false);
              await load();
            }}
          />
        )}
      </section>

      <section style={card}>
        <h2 style={cardTitle}>{t("procurement.document.title", "Documents")}</h2>

        {documents.length === 0 ? (
          <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
            {t("procurement.document.empty", "No documents yet.")}
          </p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {documents.map((d) => (
              <li key={d.id} style={docRow}>
                {d.contentType === "application/pdf" ? <Ic.pdf /> : <Ic.image />}
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13, color: "var(--ink)" }}>{d.fileName}</span>
                  <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
                    {t(`procurement.document.kind.${d.kind}`, d.kind)} · {Math.round(d.sizeBytes / 1024)} kB ·{" "}
                    {fmtDate(d.createdAt)}
                  </span>
                </span>
                <button type="button" className="btn sm" onClick={() => void download(d)}>
                  {t("procurement.document.download", "Download")}
                </button>
                {canManageDocs && hasPermission("section:procurement_contracts:full") && (
                  <button type="button" className="btn sm" onClick={() => void remove(d)}>
                    {t("procurement.document.delete", "Delete document")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {canManageDocs && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
            <Select<ProcurementContractDocumentKind>
              value={kind}
              onChange={setKind}
              ariaLabel={t("procurement.document.kindLabel", "Document kind")}
              options={DOC_KINDS.map((k) => ({
                value: k,
                label: t(`procurement.document.kind.${k}`, k),
              }))}
            />
            <input
              ref={fileInput}
              type="file"
              accept={CONTRACT_DOC_ALLOWED.join(",")}
              disabled={busy || documents.length >= CONTRACT_DOC_MAX_FILES}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
            />
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * New amendment. Every term is optional and null means "unchanged" — the backend
 * refuses an amendment that states nothing, so the submit button stays disabled
 * until at least one term is filled in.
 */
function AmendmentForm({
  contractId,
  onClose,
  onSaved,
}: {
  contractId: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [effectiveDate, setEffectiveDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [quantityT, setQuantityT] = useState("");
  const [pricePerT, setPricePerT] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const statesSomething = quantityT.trim() !== "" || pricePerT.trim() !== "";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!statesSomething) return;
    setBusy(true);
    try {
      await procurementContractsApi.createAmendment(contractId, {
        effectiveDate,
        quantityT: quantityT.trim() === "" ? null : Number(quantityT),
        pricePerT: pricePerT.trim() === "" ? null : Number(pricePerT),
        note: note.trim() === "" ? null : note.trim(),
      });
      toast.success(t("procurement.amendment.saved", "Amendment recorded."));
      await onSaved();
    } catch (err) {
      const e = err as ApiError;
      toast.error(e.code === "CONTRACT_PRICE_LOCKED"
        ? t("procurement.amendment.directorOnly", "Only a director amends a contract in force.")
        : e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
      <div style={grid}>
        <label>
          <span style={fieldLabel}>{t("procurement.amendment.effectiveDate", "Effective from")}</span>
          <input
            type="date"
            className="input"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            required
            style={{ width: "100%" }}
          />
        </label>
        <label>
          <span style={fieldLabel}>{t("procurement.contracts.list.col.quantity")}</span>
          <input
            type="number"
            min="0"
            step="0.01"
            className="input"
            value={quantityT}
            onChange={(e) => setQuantityT(e.target.value)}
            style={{ width: "100%" }}
          />
        </label>
        <label>
          <span style={fieldLabel}>{t("procurement.contracts.list.col.price")}</span>
          <input
            type="number"
            min="0"
            step="0.01"
            className="input"
            value={pricePerT}
            onChange={(e) => setPricePerT(e.target.value)}
            style={{ width: "100%" }}
          />
        </label>
      </div>
      <label>
        <span style={fieldLabel}>{t("procurement.amendment.note", "Note")}</span>
        <input
          className="input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={{ width: "100%" }}
        />
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" className="btn primary" disabled={busy || !statesSomething}>
          {t("common.save", "Save")}
        </button>
        <button type="button" className="btn" onClick={onClose}>
          {t("common.cancel", "Cancel")}
        </button>
      </div>
    </form>
  );
}

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={fieldLabel}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--ink)" }}>{value}</span>
    </div>
  );
}

const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};
const cardTitle: CSSProperties = { margin: 0, fontSize: 14, color: "var(--ink)" };
const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
const grid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 12,
};
const docRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 10px",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)",
};
const crumbMuted: CSSProperties = { color: "var(--ink-4)" };
const crumbLink: CSSProperties = { color: "var(--ink-3)", textDecoration: "none" };
const crumbCurrent: CSSProperties = { color: "var(--ink-2)", fontWeight: 600 };
